/**
 * Engine status and setup state.
 *
 * Separate from `appStore` because setup is a long-running process with its
 * own log stream, and `appStore.job` is typed for pipeline runs — a stage id
 * there is a `StageId`, and "create_venv" is not one.
 */

import { create } from "zustand";
import {
  cancelEngineSetup,
  detectEngineCandidates,
  getEngineStatus,
  onEngineSetupLog,
  onEngineSetupState,
  onEngineSetupStep,
  setEngineSelection,
  splitError,
  startEngineSetup,
  validateEngine,
} from "@/ipc/api";
import type {
  CheckoutCandidate,
  EngineSelection,
  EngineSetupStatus,
  EngineStatus,
  NativeEngineProbe,
  PythonCandidate,
} from "@/ipc/contract";

/** Keeps memory bounded on a pip install that prints tens of thousands of lines. */
const LOG_CAP = 4000;

interface EngineState {
  status: EngineStatus | null;
  loading: boolean;
  error: string | null;

  candidates: { pythons: PythonCandidate[]; checkouts: CheckoutCandidate[] } | null;
  checkoutPath: string;
  pythonPath: string;

  setup: EngineSetupStatus | null;
  log: string[];
  verifying: boolean;
  verified: NativeEngineProbe | null;

  refresh: (force?: boolean) => Promise<void>;
  detect: () => Promise<void>;
  choose: (patch: { checkoutPath?: string; pythonPath?: string }) => void;
  select: (kind: EngineSelection) => Promise<void>;
  verify: () => Promise<void>;
  install: (mode: "use-checkout" | "create-venv") => Promise<void>;
  stop: () => Promise<void>;
  subscribe: () => Promise<() => void>;
}

export const useEngineStore = create<EngineState>((set, get) => ({
  status: null,
  loading: false,
  error: null,
  candidates: null,
  checkoutPath: "",
  pythonPath: "",
  setup: null,
  log: [],
  verifying: false,
  verified: null,

  refresh: async (force = false) => {
    set({ loading: true, error: null });
    try {
      set({ status: await getEngineStatus(force), loading: false });
    } catch (error) {
      set({ loading: false, error: splitError(error).message });
    }
  },

  detect: async () => {
    try {
      const candidates = await detectEngineCandidates();
      // Preselect the best of each so the common case is one click.
      const checkout =
        get().checkoutPath ||
        candidates.checkouts.find((c) => c.missing.length === 0)?.path ||
        candidates.checkouts[0]?.path ||
        "";
      const python = get().pythonPath || candidates.pythons.find((p) => p.usable)?.path || "";
      set({ candidates, checkoutPath: checkout, pythonPath: python });
    } catch (error) {
      set({ error: splitError(error).message });
    }
  },

  choose: (patch) => set({ ...patch, verified: null }),

  select: async (kind) => {
    set({ loading: true, error: null });
    try {
      set({ status: await setEngineSelection(kind), loading: false });
    } catch (error) {
      set({ loading: false, error: splitError(error).message });
    }
  },

  verify: async () => {
    set({ verifying: true, error: null });
    try {
      const probe = await validateEngine(get().pythonPath || null, get().checkoutPath || null);
      set({ verifying: false, verified: probe });
      await get().refresh(true);
    } catch (error) {
      set({ verifying: false, error: splitError(error).message });
    }
  },

  install: async (mode) => {
    const checkoutPath = get().checkoutPath.trim();
    if (!checkoutPath) {
      set({ error: "Choose an OpenADS folder first." });
      return;
    }
    set({ error: null, log: [], verified: null });
    try {
      const setup = await startEngineSetup({
        mode,
        checkoutPath,
        hostPythonPath: mode === "create-venv" ? get().pythonPath || null : null,
      });
      set({ setup });
    } catch (error) {
      set({ error: splitError(error).message });
    }
  },

  stop: async () => {
    try {
      await cancelEngineSetup();
    } catch (error) {
      set({ error: splitError(error).message });
    }
  },

  subscribe: async () => {
    const offs = await Promise.all([
      onEngineSetupLog((e) => {
        set((s) => {
          const next = s.log.concat(e.entries.map((x) => x.line));
          return { log: next.length > LOG_CAP ? next.slice(next.length - LOG_CAP) : next };
        });
      }),
      onEngineSetupStep((step) => {
        set((s) =>
          s.setup
            ? {
                setup: {
                  ...s.setup,
                  steps: s.setup.steps.map((x) => (x.id === step.id ? step : x)),
                },
              }
            : s,
        );
      }),
      onEngineSetupState((status) => {
        set({ setup: status });
        // The engine only becomes usable at the end, so re-read it then.
        if (status.state === "completed" || status.state === "failed") {
          void get().refresh(true);
        }
      }),
    ]);
    return () => offs.forEach((off) => off());
  },
}));
